import {Component, Input} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {CompanyResponse} from '../../../../@shared/models/response.module';
import {CompanyService} from '../../../../@shared/services/company.service';
import {_COR_COY_8} from '../../../../@shared/models/input.module';
import {ImageCropResult} from '../../../../@shared/components/image-crop-selector/image-crop-selector';

@Component({
  selector: 'app-update-company-image',
  standalone: false,
  templateUrl: './update-company-image.html',
  styleUrl: './update-company-image.css'
})
export class UpdateCompanyImage {
  @Input() company: CompanyResponse
  @Input() imageType: 'header' | 'logo' = 'logo';

  selectedFile: File | null = null;
  originalFile: File | null = null;
  uploading: boolean = false;
  showCropSelector: boolean = false;

  constructor(private companyService: CompanyService,
              private modal: NgbActiveModal) {
  }

  get title(): string {
    return this.imageType === 'header' ? 'Update Header Image' : 'Update Company Logo';
  }

  get currentImageUrl(): string | null {
    return this.imageType === 'header' ? (this.company.header_image_url ?? null) : (this.company.company_logo_url ?? null);
  }

  get description(): string {
    return this.imageType === 'header'
      ? 'Upload a new header image for your company (recommended size: 1920x300)'
      : 'Upload a new logo for your company (recommended size: 400x400)';
  }

  // Get aspect ratio based on image type
  get cropAspectRatio(): number {
    if (this.imageType === 'header') {
      // Header images display at approximately 1400px x 300px
      return 1400 / 300;
    } else {
      // Logo images are displayed as squares (125-150px)
      return 1;
    }
  }

  onFileSelected(file: File) {
    this.originalFile = file;
    // Show crop selector for both header and logo images
    this.showCropSelector = true;
    this.selectedFile = null;
  }

  onCropConfirmed(result: ImageCropResult) {
    if (result.croppedImageBase64) {
      // Convert base64 to File
      this.selectedFile = this.base64ToFile(result.croppedImageBase64, this.originalFile?.name || 'cropped-image.jpg');
    }
    this.showCropSelector = false;
  }

  onCropCancelled() {
    this.showCropSelector = false;
    this.originalFile = null;
    this.selectedFile = null;
  }

  private base64ToFile(base64: string, fileName: string): File {
    const arr = base64.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], fileName, { type: mime });
  }

  onSave() {
    if (!this.selectedFile) {
      return;
    }

    this.uploading = true;
    const payload: _COR_COY_8 = {
      image_type: this.imageType,
      image: this.selectedFile
    };

    this.companyService.editCompanyFiles(payload).subscribe({
      next: () => {
        this.uploading = false;
        this.modal.close(true);
      },
      error: () => {
        this.uploading = false;
      }
    });
  }

  close() {
    this.modal.close();
  }
}
